import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { MY_GRIEVANCES, SUBMIT_GRIEVANCE } from '../graphql';
import { Ticket, Plus, Clock, CheckCircle, Search, AlertCircle } from 'lucide-react';

const CATEGORIES = ['Marks Discrepancy', 'Fee Issue', 'Certificate Request', 'Attendance Dispute', 'Library', 'Hostel', 'Other'];

const STATUS_CONFIG = {
  OPEN:      { label: 'Open',       color: 'bg-blue-100 text-blue-700',   icon: Clock },
  IN_REVIEW: { label: 'In Review',  color: 'bg-yellow-100 text-yellow-700', icon: Search },
  RESOLVED:  { label: 'Resolved',   color: 'bg-green-100 text-green-700', icon: CheckCircle },
  CLOSED:    { label: 'Closed',     color: 'bg-gray-100 text-gray-500',   icon: CheckCircle },
};

export default function GrievancePage() {
  const { data, loading } = useQuery(MY_GRIEVANCES);
  const [submitGrievance] = useMutation(SUBMIT_GRIEVANCE, { refetchQueries: [MY_GRIEVANCES] });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ category: 'Marks Discrepancy', title: '', description: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitGrievance({ variables: form });
    setForm({ category: 'Marks Discrepancy', title: '', description: '' });
    setShowForm(false);
  };

  const grievances = data?.myGrievances || [];
  const open     = grievances.filter(g => g.status === 'OPEN' || g.status === 'IN_REVIEW').length;
  const resolved = grievances.filter(g => g.status === 'RESOLVED').length;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Grievance Tracker</h1>
          <p className="text-gray-500 text-sm mt-1">Submit issues and track their resolution — no more emails into the void</p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={() => setShowForm(!showForm)}>
          <Plus size={18} /> Raise Issue
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="card !p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{grievances.length}</p>
          <p className="text-xs text-gray-500">Total</p>
        </div>
        <div className="card !p-4 text-center">
          <p className="text-2xl font-bold text-yellow-600">{open}</p>
          <p className="text-xs text-gray-500">Pending</p>
        </div>
        <div className="card !p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{resolved}</p>
          <p className="text-xs text-gray-500">Resolved</p>
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="card mb-6 space-y-4">
          <h3 className="font-semibold">Raise a Grievance</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select className="input" value={form.category} onChange={e=>setForm({...form,category:e.target.value})}>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject / Title</label>
            <input className="input" required value={form.title} onChange={e=>setForm({...form,title:e.target.value})} placeholder="Internal marks not updated for Unit 3 test" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea className="input !h-32 resize-none" required value={form.description}
              onChange={e=>setForm({...form,description:e.target.value})}
              placeholder="Describe your issue in detail — include dates, subject codes, amounts, or any reference numbers..." />
          </div>
          <div className="flex gap-3">
            <button className="btn-primary" type="submit">Submit Grievance</button>
            <button className="btn-secondary" type="button" onClick={()=>setShowForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-4 border-brand-600 border-t-transparent rounded-full" /></div>
      ) : (
        <div className="space-y-4">
          {grievances.map(g => {
            const cfg = STATUS_CONFIG[g.status];
            const Icon = cfg.icon;
            return (
              <div key={g.id} className="card">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <span className="badge bg-gray-100 text-gray-500 mr-2">{g.category}</span>
                    <span className={`badge ${cfg.color} flex items-center gap-1 inline-flex`}>
                      <Icon size={11} /> {cfg.label}
                    </span>
                  </div>
                  <span className="text-xs text-gray-400">#{g.id} · {new Date(g.createdAt).toLocaleDateString()}</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{g.title}</h3>
                <p className="text-sm text-gray-600 mb-3">{g.description}</p>
                {g.adminNote && (
                  <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
                    <p className="text-xs font-semibold text-blue-700 mb-1">Admin Response</p>
                    <p className="text-sm text-blue-800">{g.adminNote}</p>
                  </div>
                )}
                {g.status === 'OPEN' && (
                  <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                    <Clock size={11} /> Awaiting review — typically 3–5 working days
                  </p>
                )}
              </div>
            );
          })}

          {grievances.length === 0 && !showForm && (
            <div className="text-center py-16 text-gray-400">
              <Ticket size={48} className="mx-auto mb-3 opacity-30" />
              <p className="font-medium">No grievances raised</p>
              <p className="text-sm mt-1">Have an issue? Raise it here — with a trackable ticket number.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
